import { useClientStore } from '@core/client/useClientStore';
import {
  JobApplication,
  JobApplicationStatus,
} from '@core/generated/gql-types';
import { useJobApplicationStore } from '@core/jobApplication/useJobApplicationStore';
import { ratingActions } from '@core/rating/rating.action';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { useJobStore } from '@core/job/useJobStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  formatDate,
  getJobEndTimeString12h,
  getJobStartTimeString12h,
} from '@/utils/utils';
import ApplicantDocumentsSection from './ApplicantDocumentsSection';
import AssignmentRateSection from '@/modules/jobs/components/create/AssignmentRateSection';
import {
  EMPTY_ASSIGNMENT_RATE_INPUT,
  getAssignmentRateOverrides,
  type AssignmentRateInput,
} from '@/modules/jobs/components/create/assignmentRateInputs';
import useCustomAssignmentRatesAccess from '@/modules/jobs/hooks/useCustomAssignmentRatesAccess';
import type { Job } from '@core/generated/gql-types';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';

const JobApplicationItem = ({
  application,
  showClientName = true,
  showWorkerLink = true,
  showApplicantInsights = false,
  showApplicantDocuments = false,
  onJobUpdated,
}: {
  application: JobApplication;
  showClientName?: boolean;
  showWorkerLink?: boolean;
  showApplicantInsights?: boolean;
  showApplicantDocuments?: boolean;
  onJobUpdated?: (job: Job) => void;
}) => {
  const { getClientById } = useClientStore();
  const { respondToApplication, fetchJobApplicationsForJob } =
    useJobApplicationStore();
  const { fetchJobById, getJobById } = useJobStore();
  const { getWorkerById } = useWorkerStore();
  const location = useLocation();
  const job = getJobById(application?.jobId);
  const worker = getWorkerById(application.userId);
  const [workerRating, setWorkerRating] = useState<number | null>(null);
  const [isRatingLoading, setIsRatingLoading] = useState(false);
  const [isEditingAssignmentRates, setIsEditingAssignmentRates] =
    useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [assignmentRateInput, setAssignmentRateInput] =
    useState<AssignmentRateInput>(EMPTY_ASSIGNMENT_RATE_INPUT);
  const canSetCustomAssignmentRates = useCustomAssignmentRatesAccess(
    job?.tenantId,
  );
  const fetchClassificationLevelClientRates = useClassificationStore(
    (state) => state.fetchClassificationLevelClientRates,
  );
  const displayCandidateSuburbForClients = useCoreLoopConfigurationStore(
    (state) =>
      state.coreLoopConfigurationItems?.displayCandidateSuburbForClients ===
      true,
  );

  useEffect(() => {
    if (application.jobId && !job) {
      fetchJobById(application.jobId);
    }
  }, [application.jobId, fetchJobById, job]);

  useEffect(() => {
    if (canSetCustomAssignmentRates && application.clientId) {
      void fetchClassificationLevelClientRates(application.clientId);
    }
  }, [
    application.clientId,
    canSetCustomAssignmentRates,
    fetchClassificationLevelClientRates,
  ]);

  useEffect(() => {
    if (!showApplicantInsights || !application.userId) {
      return;
    }

    const loadRating = async () => {
      setIsRatingLoading(true);
      try {
        const rating = await ratingActions.fetchRatingForUserId(
          application.userId,
        );
        setWorkerRating(
          typeof rating === 'number' && Number.isFinite(rating) ? rating : null,
        );
      } catch (error) {
        console.error('Error fetching worker rating:', error);
        setWorkerRating(null);
      } finally {
        setIsRatingLoading(false);
      }
    };

    void loadRating();
  }, [showApplicantInsights, application.userId]);

  const getClientName = (clientId: string) => {
    const client = getClientById(clientId);
    return client?.name || 'Unknown';
  };

  const handleRespond = async (status: JobApplicationStatus) => {
    setIsResponding(true);
    try {
      const rateOverrides =
        status === JobApplicationStatus.Accepted
          ? getAssignmentRateOverrides(assignmentRateInput)
          : undefined;
      await respondToApplication(application.id, status, rateOverrides);
      await fetchJobApplicationsForJob(application.jobId);
      const updatedJob = await fetchJobById(application.jobId);
      if (updatedJob) {
        onJobUpdated?.(updatedJob);
      }
      setIsEditingAssignmentRates(false);
    } catch (error) {
      console.error('Error responding to application:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Unable to respond to application',
      );
    } finally {
      setIsResponding(false);
    }
  };

  const renderWorkerRating = () => {
    if (!showApplicantInsights) {
      return null;
    }

    if (isRatingLoading) {
      return <span className="text-sm text-gray-400">Loading rating...</span>;
    }

    if (workerRating == null) {
      return null;
    }

    return (
      <span className="flex items-center gap-1 text-sm text-gray-700">
        <FontAwesomeIcon icon={faStar} className="h-4 w-4 text-amber-400" />
        <span>{workerRating.toFixed(1)}</span>
      </span>
    );
  };

  const renderJobTitle = () => {
    const workerName =
      `${worker?.firstName || 'Unknown Worker'} ${worker?.lastName || ''}`.trim();
    if (location.pathname.includes('/jobs/')) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <h3
            className={
              showApplicantInsights
                ? 'text-base font-semibold text-gray-900'
                : 'font-medium'
            }
          >
            {showWorkerLink ? (
              <Link to={`/workers/${application.userId}`}>{workerName}</Link>
            ) : (
              workerName
            )}
          </h3>
          {renderWorkerRating()}
        </div>
      );
    }

    if (location.pathname.includes('/workers/')) {
      return (
        <h3 className="font-medium">
          <Link to={`/jobs/${application.jobId}`}>
            {job?.title || 'Unknown Job'}
          </Link>
        </h3>
      );
    }

    return null;
  };

  const appliedOnLabel = dayjs(parseInt(application.createdAt, 10)).format(
    'DD/MM/YYYY',
  );
  const applicantLocation = [worker?.address?.suburb, worker?.address?.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
  const shouldShowApplicantLocation =
    displayCandidateSuburbForClients && Boolean(applicantLocation);

  const renderApplicantLocation = () => {
    if (!shouldShowApplicantLocation) {
      return null;
    }

    return (
      <p
        className={`flex items-center gap-1.5 text-sm text-gray-600 ${
          showApplicantInsights ? '' : 'mt-1'
        }`}
      >
        <FontAwesomeIcon
          icon={faLocationDot}
          title="Applicant location"
          className="h-3 w-3"
        />
        {applicantLocation}
      </p>
    );
  };

  const renderApplicationDates = () => {
    const dateClassName = showApplicantInsights
      ? 'text-sm text-gray-600'
      : 'mt-1 text-sm text-gray-600';

    return (
      <div className={showApplicantInsights ? 'mt-2 space-y-1' : undefined}>
        <p className={dateClassName}>Applied on: {appliedOnLabel}</p>
        <p className={dateClassName}>
          Start date:{' '}
          {job
            ? `${getJobStartTimeString12h(job)}, ${formatDate(job.startDate ?? '')}`
            : '—'}
        </p>
        <p className={dateClassName}>
          End date:{' '}
          {job
            ? `${getJobEndTimeString12h(job)}, ${formatDate(job.endDate ?? '')}`
            : '—'}
        </p>
      </div>
    );
  };

  const renderStatusActions = () => (
    <div className="flex items-center gap-2">
      <span
        className={`rounded-full px-2 py-1 text-xs font-medium ${
          application.itemStatus === JobApplicationStatus.Pending
            ? 'bg-yellow-100 text-yellow-800'
            : application.itemStatus === JobApplicationStatus.Accepted
              ? 'bg-green-100 text-green-800'
              : application.itemStatus === JobApplicationStatus.Rejected
                ? 'bg-red-100 text-red-800'
                : application.itemStatus === JobApplicationStatus.Withdrawn
                  ? 'bg-gray-100 text-gray-800'
                  : 'bg-gray-100 text-gray-800'
        }`}
      >
        {application.itemStatus || 'Unknown'}
      </span>

      {application.itemStatus === JobApplicationStatus.Pending && (
        <Menu as="div" className="relative">
          <Menu.Button className="rounded-full p-1 hover:bg-gray-100">
            <EllipsisVerticalIcon
              className="h-5 w-5 text-gray-500"
              aria-hidden="true"
            />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-10 mt-2 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-hidden">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => {
                      if (canSetCustomAssignmentRates && worker && job) {
                        setIsEditingAssignmentRates(true);
                        return;
                      }
                      void handleRespond(JobApplicationStatus.Accepted);
                    }}
                    className={`${
                      active ? 'bg-gray-50' : ''
                    } w-full px-4 py-2 text-left text-sm text-green-600`}
                  >
                    Accept Application
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => handleRespond(JobApplicationStatus.Rejected)}
                    className={`${
                      active ? 'bg-gray-50' : ''
                    } w-full px-4 py-2 text-left text-sm text-red-600`}
                  >
                    Reject Application
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      )}
    </div>
  );

  const renderAssignmentRateEditor = () => {
    if (!isEditingAssignmentRates || !worker || !job) {
      return null;
    }

    return (
      <div>
        <AssignmentRateSection
          worker={worker}
          clientId={application.clientId}
          classificationId={job.classificationId}
          rateInput={assignmentRateInput}
          ordinaryWorkerPayRateCents={application.workerRateInCents}
          ordinaryClientPayRateCents={application.clientRateInCents}
          onChange={(updates) =>
            setAssignmentRateInput((currentRateInput) => ({
              ...currentRateInput,
              ...updates,
            }))
          }
        />
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            disabled={isResponding}
            onClick={() => setIsEditingAssignmentRates(false)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isResponding}
            onClick={() => void handleRespond(JobApplicationStatus.Accepted)}
            className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {isResponding ? 'Accepting...' : 'Accept application'}
          </button>
        </div>
      </div>
    );
  };

  const renderComment = () => {
    const comment = application.coverLetter?.trim();
    if (!comment) {
      return null;
    }

    return (
      <blockquote className="mt-3 text-sm break-words text-gray-700 italic">
        “{comment}”
      </blockquote>
    );
  };

  if (showApplicantInsights) {
    return (
      <div
        key={application.id}
        className="rounded-lg border border-gray-200 bg-white p-5"
      >
        <div className="flex items-start justify-between">
          <div>
            {renderJobTitle()}
            {renderApplicantLocation()}
            {renderApplicationDates()}
            {application.userId && showApplicantDocuments && (
              <ApplicantDocumentsSection userId={application.userId} />
            )}
          </div>
          {renderStatusActions()}
        </div>
        {renderComment()}
        {renderAssignmentRateEditor()}
      </div>
    );
  }

  return (
    <div key={application.id} className="rounded-lg border p-4">
      <div className="flex items-start justify-between">
        <div>
          {renderJobTitle()}
          {renderApplicantLocation()}

          {showClientName && (
            <p className="mt-1 text-sm text-gray-600">
              Client:{' '}
              <Link to={`/clients/${application.clientId}`}>
                {getClientName(application.clientId)}
              </Link>
            </p>
          )}
          {renderApplicationDates()}
        </div>
        {renderStatusActions()}
      </div>
      {renderComment()}
      {renderAssignmentRateEditor()}
    </div>
  );
};

export default JobApplicationItem;
